import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import db_config from '../../configs/db_config';
import { db } from '../../libs/firebase';
import { collection, doc, getDocs, onSnapshot, query, setDoc, updateDoc, where } from '../../libs/firestore-wrapper';
import { getStadiumConfig } from '../../modals/order';
import { AllPreorderRulesState, ClientEmailContactingState, FoodTruckReminderState, PreorderRuleState, TimeUnit } from '../../pages/PreoderRules/PreorderRules.types';
import { IAstrolabReminders, IClientEmailContacting, IPreorderRules, IStadium } from '../../pages/Settings/settings.types';
import { getFoodTruckPreorderRuleConstraintUpdates } from '../../utils/preorderRules';
import { useAuthContext } from '../AuthContext/AuthContext';
import { useSettingsContext } from '../SettingsContext';

interface PreorderRulesContextType {
  currentValues: AllPreorderRulesState;
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  isLoading: boolean;
  resetKey: number;
  handleSave: () => Promise<void>;
  handleCancel: () => void;
  updatePreorderRule: (values: PreorderRuleState) => void;
  updateClientEmailContacting: (values: ClientEmailContactingState) => void;
  updateAstrolabReminder: (values: FoodTruckReminderState & { isValid: boolean }) => void;
  stadiumData: IStadium | null;
  isFormValid: boolean;
}

const PreorderRulesContext = createContext<PreorderRulesContextType | undefined>(undefined);

// Helper functions (moved from Page)
const typeToUnit = (type: string | undefined, fallback: TimeUnit = 'day'): TimeUnit => {
  if (type === 'minutes' || type === 'minute' || type === 'mins') return 'minute';
  if (type === 'hours' || type === 'hour') return 'hour';
  if (type === 'days' || type === 'day') return 'day';
  return fallback;
};

const unitToType = (unit: TimeUnit): string => {
  if (unit === 'minute') return 'minutes';
  if (unit === 'hour') return 'hours';
  if (unit === 'day') return 'days';
  return 'days';
};

const convertAPIToPreorderRuleState = (api: IPreorderRules | undefined): PreorderRuleState => {
  const defaults: PreorderRuleState = {
    acceptanceSince: { value: 30, unit: 'day', pickerValue: '00:00' },
    acceptanceUntil: { value: 0, unit: 'day', pickerValue: '00:00' },
    timeSlotsSince: { value: 30, unit: 'minute' },
    orderDeliverableUntil: { value: 2, unit: 'hour' },
  };

  if (!api) return defaults;

  return {
    acceptanceSince: {
      value: api.acceptance?.since?.time ?? defaults.acceptanceSince.value,
      unit: typeToUnit(api.acceptance?.since?.type),
      pickerValue: api.acceptance?.since?.at || defaults.acceptanceSince.pickerValue,
    },
    acceptanceUntil: {
      value: api.acceptance?.until?.time ?? defaults.acceptanceUntil.value,
      unit: typeToUnit(api.acceptance?.until?.type),
      pickerValue: api.acceptance?.until?.at || defaults.acceptanceUntil.pickerValue,
    },
    timeSlotsSince: {
      value: api.time_slot?.time ?? defaults?.timeSlotsSince?.value,
      unit: typeToUnit(api.time_slot?.type, defaults.timeSlotsSince.unit),
    },
    orderDeliverableUntil: {
      value: api.order_deliberable?.until?.time ?? defaults.orderDeliverableUntil.value,
      unit: typeToUnit(api.order_deliberable?.until?.type, defaults.orderDeliverableUntil.unit),
    },
  };
};

const convertAPIToClientEmailState = (api: IClientEmailContacting | undefined): ClientEmailContactingState => {
  const defaults: ClientEmailContactingState = {
    his_success: true,
    his_deliver_unsuccess: true,
    his_delivered: true,
    other_success: true,
    other_deliver_unsuccess: true,
    other_delivered: true,
    gift_success: true,
    gift_deliver_unsuccess: true,
    gift_delivered: true,
  };

  if (!api) return defaults;

  return {
    his_success: api.order_for_himher?.order_success ?? defaults.his_success,
    his_deliver_unsuccess: api.order_for_himher?.order_deliver_unsuccess ?? defaults.his_deliver_unsuccess,
    his_delivered: api.order_for_himher?.order_delivered ?? defaults.his_delivered,
    other_success: api.order_for_other?.order_success ?? defaults.other_success,
    other_deliver_unsuccess: api.order_for_other?.order_deliver_unsuccess ?? defaults.other_deliver_unsuccess,
    other_delivered: api.order_for_other?.order_delivered ?? defaults.other_delivered,
    gift_success: api.gift_notification?.order_success ?? defaults.gift_success,
    gift_deliver_unsuccess: api.gift_notification?.order_deliver_unsuccess ?? defaults.gift_deliver_unsuccess,
    gift_delivered: api.gift_notification?.order_delivered ?? defaults.gift_delivered,
  };
};

const convertAPIToAstrolabReminderState = (api: IAstrolabReminders | undefined): FoodTruckReminderState => {
  const defaults: FoodTruckReminderState = {
    emails: [],
  };

  if (!api) return defaults;

  return {
    emails: api.emails || [],
  };
};

export const PreorderRulesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { userInfo } = useAuthContext();
  const [stadiumData, setStadiumData] = useState<IStadium | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const { stadiumData: _stadiumData } = useSettingsContext();

  const getDefaultValues = useCallback(
    (): AllPreorderRulesState => ({
      preorderRule: {
        acceptanceSince: { value: 30, unit: 'day', pickerValue: '00:00' },
        acceptanceUntil: { value: 0, unit: 'day', pickerValue: '00:00' },
        timeSlotsSince: { value: 30, unit: 'minute' },
        orderDeliverableUntil: { value: 2, unit: 'hour' },
      },
      clientEmailContacting: {
        his_success: false,
        his_deliver_unsuccess: false,
        his_delivered: false,
        other_success: false,
        other_deliver_unsuccess: false,
        other_delivered: false,
        gift_success: false,
        gift_deliver_unsuccess: false,
        gift_delivered: false,
      },
      astrolabReminder: {
        emails: [],
      },
    }),
    []
  );

  const [currentValues, setCurrentValues] = useState<AllPreorderRulesState>(getDefaultValues);
  const initialValuesRef = useRef<AllPreorderRulesState>(getDefaultValues());
  const [isAstrolabEmailsValid, setIsAstrolabEmailsValid] = useState(true);

  useEffect(() => {
    const stadiumId = userInfo?.stadium_id;
    setIsLoading(true);

    if (_stadiumData?.id) {
      setStadiumData(_stadiumData);
      setIsLoading(false);
      return;
    }

    if (!stadiumId) {
      setIsLoading(false);
      return;
    }

    const stadiumRef = doc(getStadiumConfig(), stadiumId);
    const unsubscribe = onSnapshot(
      stadiumRef,
      (docSnap: any) => {
        if (docSnap.exists()) {
          setStadiumData({ ...(docSnap.data() as IStadium), id: docSnap.id });
        }
        setIsLoading(false);
      },
      (error) => {
        console.error('Error fetching stadium data:', error);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userInfo?.stadium_id, _stadiumData]);

  useEffect(() => {
    if (!isLoading && stadiumData && !isInitialized) {
      const groupedData = (stadiumData as any).pre_order_rules;

      const preorderRuleFromAPI = convertAPIToPreorderRuleState(groupedData?.preorder_rules || stadiumData.preorder_rules);
      const clientEmailFromAPI = convertAPIToClientEmailState(groupedData?.client_email_contacting || stadiumData.client_email_contacting);
      const astrolabFromAPI = convertAPIToAstrolabReminderState(groupedData?.astrolab_reminders || stadiumData.astrolab_reminders);

      const initialValues = {
        preorderRule: preorderRuleFromAPI,
        clientEmailContacting: clientEmailFromAPI,
        astrolabReminder: astrolabFromAPI,
      };

      setCurrentValues(initialValues);
      initialValuesRef.current = initialValues;
      setIsInitialized(true);
    } else if (!isLoading && !stadiumData && !isInitialized) {
      setIsInitialized(true);
    }
  }, [isLoading, stadiumData, isInitialized]);

  const hasUnsavedChanges = useMemo(() => {
    return JSON.stringify(currentValues) !== JSON.stringify(initialValuesRef.current);
  }, [currentValues]);

  const updatePreorderRule = useCallback((values: PreorderRuleState) => {
    setCurrentValues((prev) => ({ ...prev, preorderRule: values }));
  }, []);

  const updateClientEmailContacting = useCallback((values: ClientEmailContactingState) => {
    setCurrentValues((prev) => ({ ...prev, clientEmailContacting: values }));
  }, []);

  const updateAstrolabReminder = useCallback((values: FoodTruckReminderState & { isValid: boolean }) => {
    const { isValid, ...reminderData } = values;
    setIsAstrolabEmailsValid(isValid);
    setCurrentValues((prev) => ({ ...prev, astrolabReminder: reminderData }));
  }, []);

  const handleSave = async () => {
    const stadiumId = userInfo?.stadium_id;
    if (!stadiumId) return;

    // Prevent save if astrolab emails are invalid
    if (!isAstrolabEmailsValid) {
      throw new Error('Please fix validation errors before saving');
    }

    setIsSaving(true);
    try {
      const preorderRulesData: IPreorderRules = {
        acceptance: {
          since: {
            at: currentValues.preorderRule.acceptanceSince.pickerValue,
            time: currentValues.preorderRule.acceptanceSince.value,
            type: unitToType(currentValues.preorderRule.acceptanceSince.unit),
          },
          until: {
            at: currentValues.preorderRule.acceptanceUntil.pickerValue,
            time: currentValues.preorderRule.acceptanceUntil.value,
            type: unitToType(currentValues.preorderRule.acceptanceUntil.unit),
          },
        },
        time_slot: {
          time: currentValues.preorderRule.timeSlotsSince.value,
          type: unitToType(currentValues.preorderRule.timeSlotsSince.unit),
        },
        order_deliberable: {
          since: {
            time: 0,
            type: 'minutes',
          },
          until: {
            time: currentValues.preorderRule.orderDeliverableUntil.value,
            type: unitToType(currentValues.preorderRule.orderDeliverableUntil.unit),
          },
        },
      };

      const clientEmailData: IClientEmailContacting = {
        gift_notification: {
          order_success: currentValues.clientEmailContacting.gift_success,
          order_deliver_unsuccess: currentValues.clientEmailContacting.gift_deliver_unsuccess,
          order_delivered: currentValues.clientEmailContacting.gift_delivered,
        },
        order_for_himher: {
          order_deliver_unsuccess: currentValues.clientEmailContacting.his_deliver_unsuccess,
          order_delivered: currentValues.clientEmailContacting.his_delivered,
          order_success: currentValues.clientEmailContacting.his_success,
        },
        order_for_other: {
          order_deliver_unsuccess: currentValues.clientEmailContacting.other_deliver_unsuccess,
          order_delivered: currentValues.clientEmailContacting.other_delivered,
          order_success: currentValues.clientEmailContacting.other_success,
        },
      };

      const alReminder = currentValues.astrolabReminder;
      const astrolabData: IAstrolabReminders = {
        emails: alReminder.emails || [],
      };

      const stadiumRef = doc(getStadiumConfig(), stadiumId);

      await setDoc(
        stadiumRef,
        {
          pre_order_rules: {
            preorder_rules: preorderRulesData,
            client_email_contacting: clientEmailData,
            astrolab_reminders: astrolabData,
          },
        },
        { merge: true }
      );

      const foodTrucksQuery = query(collection(db, db_config.DB_TABLE_RESTAURANT_CONFIG), where('stadium_id', '==', stadiumId));
      const foodTrucksSnapshot = await getDocs(foodTrucksQuery);

      const updatePromises: Promise<void>[] = [];

      foodTrucksSnapshot.docs.forEach((foodTruckDoc) => {
        const foodTruckData = foodTruckDoc.data();
        const foodTruckPreorderRules = foodTruckData?.preorder_rules;
        const constraintUpdates = getFoodTruckPreorderRuleConstraintUpdates(foodTruckPreorderRules, preorderRulesData);

        if (Object.keys(constraintUpdates).length > 0) {
          const foodTruckRef = doc(db, db_config.DB_TABLE_RESTAURANT_CONFIG, foodTruckDoc.id);
          updatePromises.push(updateDoc(foodTruckRef, constraintUpdates));
        }
      });

      if (updatePromises.length > 0) {
        await Promise.all(updatePromises);
      }

      initialValuesRef.current = { ...currentValues };
      setResetKey((prev) => prev + 1);
    } catch (error) {
      console.error('Error saving preorder rules:', error);
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = useCallback(() => {
    setCurrentValues({ ...initialValuesRef.current });
    setResetKey((prev) => prev + 1);
  }, []);

  return (
    <PreorderRulesContext.Provider
      value={{
        currentValues,
        hasUnsavedChanges,
        isSaving,
        isLoading: isLoading || !isInitialized,
        resetKey,
        handleSave,
        handleCancel,
        updatePreorderRule,
        updateClientEmailContacting,
        updateAstrolabReminder,
        stadiumData,
        isFormValid: isAstrolabEmailsValid,
      }}
    >
      {children}
    </PreorderRulesContext.Provider>
  );
};

export const usePreorderRules = () => {
  const context = useContext(PreorderRulesContext);
  if (context === undefined) {
    throw new Error('usePreorderRules must be used within a PreorderRulesProvider');
  }
  return context;
};
